<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :is-valid="isModalValid"
    :loading="computedIsLoading"
    :title="title"
    min-body-height="55vh"
    dialog-width="80%"
    header-shadow
    :agree-text="agreeText"
    @agree="agree"
    no-paddings-x
  >
    <template #body>
      <v-col cols="12" v-if="mapPanelDataFetched">
        <v-form v-model="isValidName" ref="formName">
          <v-row no-gutters class="border-bottom pb-4" align="center">
            <v-col cols="auto" class="text-left pb-5 pl-4">
              <span> {{ $i18n.t('sidebar.locationLab.reportName') }}: </span>
            </v-col>
            <v-col style="max-width: 275px !important; margin-left: 100px">
              <data-input
                v-model="computedReport.name"
                :data-type="{ name: 'text' }"
                :rules="[v => !!v || $i18n.t('rules.required')]"
              />
            </v-col>
          </v-row>
        </v-form>
        <v-row no-gutters justify="center" align="center" class="py-6 border-bottom">
          <v-col
            class="column-title"
            :class="{ 'settings-col': columnTitle.name === 'settings' }"
            v-for="columnTitle of columnsTitles"
            :key="columnTitle.name"
          >
            <span>
              {{ $i18n.t(columnTitle.translatePath) }}
            </span>
          </v-col>
        </v-row>
        <v-form v-model="isValid" ref="form">
          <v-row no-gutters justify="center" align="center" class="pt-6 border-bottom">
            <v-col>
              <data-input
                class="px-5 py-0 ma-0"
                v-model="newStatistic.name"
                :data-type="{ name: 'text' }"
                :rules="[v => !!v || $i18n.t('rules.required')]"
              />
            </v-col>
            <v-col>
              <data-input
                class="px-5 py-0 ma-0"
                v-model="newStatistic.layer"
                :data-type="{ name: 'select' }"
                :items="featuresLayers"
                item-text="name"
                item-value="id"
                :rules="[v => !!v || $i18n.t('rules.required')]"
                @change="delete newStatistic.attribute_name"
              />
            </v-col>
            <v-col>
              <data-input
                class="px-5 py-0 ma-0"
                v-model="newStatistic.type"
                :data-type="{ name: 'select' }"
                :items="statistics"
                item-text="verbose_name"
                item-value="name"
                :rules="[v => !!v || $i18n.t('rules.required')]"
                @change="delete newStatistic.attribute_name"
              />
            </v-col>
            <v-col>
              <data-input
                class="px-5 py-0 ma-0"
                v-model="newStatistic.attribute_name"
                :data-type="{ name: 'select' }"
                :items="
                  newStatistic.layer && newStatistic.type ? getAttributes(newStatistic.layer, newStatistic.type) : []
                "
                item-text="label"
                item-value="attribute"
                :rules="[v => !!v || $i18n.t('rules.required')]"
                :disabled="!newStatistic.layer || !newStatistic.type"
              />
            </v-col>
            <v-col class="settings-col pb-6">
              <v-icon
                :style="`cursor: ${isValid ? 'pointer' : 'default'}`"
                :color="isValid ? 'success' : 'grey'"
                @click="pushStatistic"
              >
                mdi-check
              </v-icon>
            </v-col>
          </v-row>
        </v-form>
        <span v-for="(statistic, idx) of computedReport.statistics" :key="idx">
          <v-form v-model="isValidEditing" form="formEditing">
            <v-row no-gutters align="center" class="border-bottom justify-space-between" style="min-height: 85px">
              <template v-if="statistic.isEditing">
                <v-col class="pt-5">
                  <data-input
                    class="px-5 py-0 ma-0"
                    v-model="statistic.name"
                    :data-type="{ name: 'text' }"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                  />
                </v-col>
                <v-col class="pt-5">
                  <data-input
                    class="px-5 py-0 ma-0"
                    v-model="statistic.layer"
                    :data-type="{ name: 'select' }"
                    :items="$_objectToArray(featuresLayers)"
                    item-text="name"
                    item-value="id"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                    @change="delete statistic.attribute_name"
                  />
                </v-col>
                <v-col class="pt-5">
                  <data-input
                    class="px-5 py-0 ma-0"
                    v-model="statistic.type"
                    :data-type="{ name: 'select' }"
                    :items="statistics"
                    item-text="verbose_name"
                    item-value="name"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                  />
                </v-col>
                <v-col class="pt-5">
                  <data-input
                    class="px-5 py-0 ma-0"
                    v-model="statistic.attribute_name"
                    :data-type="{ name: 'select' }"
                    :items="statistic.layer && statistic.type ? getAttributes(statistic.layer, statistic.type) : []"
                    item-text="label"
                    item-value="attribute"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                    :disabled="!statistic.layer || !statistic.type"
                    @change="delete newStatistic.attribute_name"
                  />
                </v-col>
              </template>
              <template v-else>
                <v-col class="column-element-text">
                  {{ statistic.name }}
                </v-col>
                <v-col class="column-element-text">
                  {{ featuresLayers.find(layer => layer.id === statistic.layer).name }}
                </v-col>
                <v-col class="column-element-text">
                  {{ statistics.find(stat => stat.name === statistic.type).verbose_name }}
                </v-col>
                <v-col class="column-element-text">
                  {{
                    getAttributes(statistic.layer, statistic.type).find(
                      attr => attr.attribute === statistic.attribute_name
                    ).label
                  }}
                </v-col>
              </template>
              <v-col class="settings-col" v-if="statistic.isEditing">
                <v-icon
                  :color="isValidEditing ? 'success' : 'grey'"
                  @click="editStatisticAgree(idx)"
                  :style="`cursor: ${isValidEditing ? 'pointer' : 'default'}`"
                >
                  mdi-check
                </v-icon>
                <v-icon color="error" @click="editStatisticDisagree(idx)">
                  mdi-close
                </v-icon>
              </v-col>
              <v-col class="settings-col" v-else>
                <v-icon @click="editStatistic(idx)">
                  mdi-pencil
                </v-icon>
                <v-icon @click="deleteStatistic(idx)">
                  mdi-delete
                </v-icon>
              </v-col>
            </v-row>
          </v-form>
        </span>
      </v-col>
      <main-loader v-else />
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import MainLargeDialog from '@/components/MainLargeDialog';
export default {
  name: 'LocationLabReportDialog',
  components: { DataInput, MainLargeDialog, MainLoader },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    new: {
      type: Boolean,
      default: false,
    },
    report: {
      type: Object,
    },
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    layers: get('layers/layers'),
    layersMetaData: get('layers/metadata'),
    statistics: get('admin/modulesMapping@location_lab.statistics'),
    featuresLayers() {
      return this.$_objectToArray(this.layers).filter(layer => layer.type === 'features_layer');
    },
    computedIsLoading: {
      get() {
        return this.isLoading;
      },
      set(nV) {
        this.$emit('update:isLoading', nV);
      },
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    computedReport: {
      get() {
        return this.new ? this.payload : this.report;
      },
      set(nV) {
        if (this.new) {
          this.payload = nV;
        } else {
          this.$emit('update:report', nV);
        }
      },
    },
    isAnyStatisticEditing() {
      return Boolean(this.computedReport?.statistics.find(stat => stat.isEditing));
    },
    isModalValid() {
      if (this.isAnyStatisticEditing) {
        return false;
      }
      return Boolean(this.computedReport?.statistics.length > 0 && this.isValidName);
    },
    agreeText() {
      return this.new ? 'dialog.agreeAdd' : 'dialog.agreeSave';
    },
    title() {
      return this.new
        ? this.$i18n.t('sidebar.locationLab.addLocationLabReport')
        : this.$i18n.t('sidebar.locationLab.editLocationLabReport');
    },
  },
  data: () => ({
    newStatistic: {},
    payload: { name: '', statistics: [] },
    columnsTitles: [
      {
        name: 'name',
        translatePath: 'dialog.statisticName',
      },
      {
        name: 'layer',
        translatePath: 'dialog.layer',
      },
      {
        name: 'type',
        translatePath: 'dialog.statistic',
      },
      {
        name: 'attribute_name',
        translatePath: 'dialog.attribute',
      },
      {
        name: 'settings',
      },
    ],
    isValid: false,
    isValidEditing: false,
    isValidName: false,
  }),
  methods: {
    addLocationLabReport: call('locationLab/addLocationLabReport'),
    editLocationLabReport: call('locationLab/editLocationLabReport'),
    agree() {
      this.new ? this.add() : this.edit();
    },
    deleteStatistic(idx) {
      this.computedReport.statistics.splice(idx, 1);
    },
    editStatistic(idx) {
      const currentStatistic = JSON.parse(JSON.stringify(this.computedReport.statistics[idx]));
      currentStatistic.copy = this.computedReport.statistics[idx];
      currentStatistic.isEditing = true;

      this.$set(this.computedReport.statistics, idx, currentStatistic);
      this.$forceUpdate();
    },
    editStatisticAgree(idx) {
      if (!this.isValidEditing) {
        return;
      }
      let currentStatistic = JSON.parse(JSON.stringify(this.computedReport.statistics[idx]));
      delete currentStatistic.copy;
      delete currentStatistic.isEditing;

      this.$set(this.computedReport.statistics, idx, currentStatistic);
      this.$forceUpdate();
    },
    editStatisticDisagree(idx) {
      let currentStatistic = JSON.parse(JSON.stringify(this.computedReport.statistics[idx]));
      currentStatistic = currentStatistic.copy;

      this.$set(this.computedReport.statistics, idx, currentStatistic);
      this.$forceUpdate();
    },
    pushStatistic() {
      if (!this.isValid) {
        return;
      }
      this.computedReport.statistics.push(this.newStatistic);
      this.newStatistic = {};
      this.$refs?.form?.resetValidation();
    },
    getAttributes(layer, statisticType) {
      const allowedDataTypes = this.statistics.find(statistic => statistic.name === statisticType).allowed_data_types;
      const layerMetadata = this.featuresLayers.find(lay => lay.id === layer);
      const layerAttributes = this.$_flattenStructure(layerMetadata.form_schema.elements);
      const dataSourceAttributes = this.layersMetaData[layerMetadata.data_source_name].attributes_schema.attributes;
      return layerAttributes.filter(attr => {
        const dataSourceAttribute = dataSourceAttributes.find(a => a.name === attr.attribute);
        const dataSourceAttributeDataTypeName = dataSourceAttribute.data_type.name;
        return allowedDataTypes.includes(dataSourceAttributeDataTypeName);
      });
    },
    async add() {
      this.computedIsLoading = true;
      try {
        await this.addLocationLabReport(this.computedReport);
        this.computedIsVisible = false;
        this.computedIsLoading = false;
      } catch {
        this.computedIsLoading = false;
      }
    },
    async edit() {
      this.computedIsLoading = true;
      try {
        const { id, name, statistics } = this.computedReport;
        const updated_statistics = statistics.filter(statistic => statistic.id || statistic.id == 0);
        const created_statistics = statistics.filter(statistic => !statistic.id && statistic.id != 0);
        const data = {
          id,
          payload: {
            name,
            updated_statistics,
            created_statistics,
          },
        };
        await this.editLocationLabReport(data);
        this.computedIsVisible = false;
        this.computedIsLoading = false;
      } catch {
        this.computedIsLoading = false;
      }
    },
  },
  watch: {
    computedIsVisible(nV) {
      if (!nV) {
        this.payload = { name: '', statistics: [] };
        this.newStatistic = {};
        this.$refs?.form?.resetValidation();
        this.$refs?.formName?.resetValidation();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.column-title {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}
.border-bottom {
  @include border(bottom, 1px);
}
.border-top {
  @include border(top, 1px);
}
.settings-col {
  max-width: 125px;
}
</style>
